import React, { useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import FormContainer from '../../../FormContainer';
import ActionsMenu from '../../../../ActionsMenu';

import { useUiContext } from '../../../../../context/UiContext';
import LienHolderService from '../../../../../services/LienHolderService';
import useLienHolderForm from '../../../../../forms/lender/lienHolder';
import UploadService from '../../../../../services/UploadService';

export default function EditLienHolder({ lenderId }) {
  const [isDownloadinWireInstructions, setIsDownloadingWireInstructions] =
    useState(false);

  const { showToast, showApiErrorToast } = useUiContext();

  const { lienHolderId } = useParams();
  const navigate = useNavigate();

  const { data: lienHolder, refetch } = useQuery({
    queryKey: ['lienHolder', lienHolderId],
    queryFn: () => LienHolderService.getLienHolder(lienHolderId),
    enabled: !!lienHolderId,
  });

  const uploadDocument = async (doc) => {
    const uploadedDoc = await UploadService.uploadAttachment(doc);

    return uploadedDoc;
  };

  const { mutate: updateLienHolder, isPending: isUpdating } = useMutation({
    mutationFn: async (formValues) => {
      const data = {
        formValues,
        id: lienHolderId,
        lenderId: lienHolder?.lender_id,
      };

      if (formValues.wire_instructions) {
        data.formValues.wire_instructions = await uploadDocument(
          formValues.wire_instructions
        );
      }

      return LienHolderService.updateLienHolder(data);
    },
    onSuccess: () => {
      showToast({
        text: 'Lien Holder updated successfully',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: createLienHolder, isPending: isCreating } = useMutation({
    mutationFn: async (formValues) => {
      const data = {
        formValues,
        lenderId,
      };

      if (formValues.wire_instructions) {
        data.formValues.wire_instructions = await uploadDocument(
          formValues.wire_instructions
        );
      }

      return LienHolderService.createLienHolder({ formValues, lenderId });
    },
    onSuccess: (lHolder) => {
      showToast({
        text: 'Lien Holder created successfully',
        type: 'success',
      });
      navigate(
        `/lenders/${lenderId}/lien-holders/${lHolder.id}?tab=Lien Holder`
      );
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const isEdit = lienHolderId !== 'new';

  const lienHolderForm = useLienHolderForm(
    isEdit ? updateLienHolder : createLienHolder,
    lienHolder,
    isEdit
  );

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <FormContainer
        headerRight={
          isEdit ? (
            <ActionsMenu
              isLoading={isDownloadinWireInstructions}
              actions={[
                {
                  disabled: !lienHolder?.wire_instructions,
                  actionTitle: 'Download Wire Instructions',
                  onClick: async () => {
                    try {
                      setIsDownloadingWireInstructions(true);

                      const wireInstructions =
                        await LienHolderService.getLienHolderWireInstructions(
                          lienHolderId
                        );

                      const downloadLink = document.createElement('a');

                      downloadLink.href = wireInstructions;
                      downloadLink.download =
                        lienHolder?.wire_instructions?.name ||
                        'wire_instructions.pdf';
                      downloadLink.target = '_blank';
                      downloadLink.rel = 'noopener noreferrer';

                      document.body.appendChild(downloadLink);

                      downloadLink.click();
                    } catch (error) {
                      showApiErrorToast(error);
                    } finally {
                      setIsDownloadingWireInstructions(false);
                    }
                  },
                },
              ]}
            />
          ) : null
        }
        form={lienHolderForm}
        noBillComId
        isSubmitting={isCreating || isUpdating}
      />
    </div>
  );
}
